import { Button, Card, CardPreview, Checkbox, Dropdown, Field, MessageBar, MessageBarBody, Option, Spinner, Text, Title3 } from "@fluentui/react-components";
import { Tendering } from "../../../models/tendering";
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from "react";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import { getUsersByModule } from "../../../services/usersService";
import { User } from "../../../models/users";

interface TenderingVisibilityProps {
  tenderingDocument: Tendering;
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

const TenderingVisibility: FC<TenderingVisibilityProps> = ({
  tenderingDocument,
  updateTenderingData,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean>(false);

  const [systemUsers, setSystemUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const isHidden = !!(tenderingDocument.properties.hideTenderList);

  useEffect(() => {
    const fetchSystemUsers = async () => {
      try {
        // Module 1 holds the system users (Skånes kommuner)
        const usersData = await getUsersByModule(1);
        setSystemUsers(usersData.map((ud: any) => ud.user));
      } catch (error) {
        console.error("Error fetching system users:", error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchSystemUsers();
  }, []);

  // No stored list means no restriction, i.e. every system user is selected by default
  const allowedIDs = tenderingDocument.properties.allowedSystemUserIDs;
  const selectedUserIDs = useMemo(
    () => allowedIDs ?? systemUsers.map(u => u.userID),
    [allowedIDs, systemUsers]
  );

  const userName = (u: User) => `${u.firstName} ${u.lastName}`;

  const updateAllowedUsers = (ids: number[]) => {
    // Everyone selected is stored as "no restriction" so system users added later still get access
    const allSelected = systemUsers.length > 0 && systemUsers.every(u => ids.includes(u.userID));
    updateTenderingData((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        allowedSystemUserIDs: allSelected ? undefined : ids
      }
    }));
  };

  const handleToggle = (_e: any, data: { checked: boolean | "mixed" }) => {
    setPendingValue(!!data.checked);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    updateTenderingData((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        hideTenderList: pendingValue
      }
    }));
    setConfirmOpen(false);
  };

  return (
    <Card className="flex-1 w-2xl">
      <CardPreview>
        <Title3>Synlighet</Title3>
      </CardPreview>
      <div className="flex flex-col gap-4 p-2">
        <MessageBar className="p-2" intent="info">
          <MessageBarBody>
            <Text>
              <strong>Dölj verksamhetslista</strong> innebär att leverantörernas verksamhetslista
              inte visas för kommunerna i ramavtalet. Kommuner kan fortfarande navigera till
              ramavtalet, men listan över anslutna leverantörer och deras verksamhetsinformation
              döljs.
            </Text>
          </MessageBarBody>
        </MessageBar>
        <Checkbox
          label="Dölj verksamhetslista"
          checked={isHidden}
          onChange={handleToggle}
        />
        <MessageBar className="p-2" intent="info">
          <MessageBarBody>
            <Text>
              <strong>Systemanvändare med åtkomst</strong> styr vilka systemanvändare som kan se
              ramavtalet. Som standard är alla systemanvändare valda. Användare som inte är valda
              ser inte ramavtalet efter nästa inloggning.
            </Text>
          </MessageBarBody>
        </MessageBar>
        {loadingUsers ? (
          <Spinner size="small" label="Hämtar systemanvändare..." />
        ) : (
          <Field label="Systemanvändare med åtkomst">
            <Dropdown
              multiselect
              placeholder="Välj användare"
              value={
                selectedUserIDs.length === systemUsers.length
                  ? "Alla systemanvändare"
                  : systemUsers.filter(u => selectedUserIDs.includes(u.userID)).map(userName).join(", ")
              }
              selectedOptions={selectedUserIDs.map(String)}
              onOptionSelect={(_e, data) => updateAllowedUsers(data.selectedOptions.map(Number))}
            >
              {systemUsers.map(u => (
                <Option key={u.userID} value={String(u.userID)} text={userName(u)}>
                  {userName(u)} ({u.email})
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
        <div className="flex gap-2">
          <Button size="small" onClick={() => updateAllowedUsers(systemUsers.map(u => u.userID))} disabled={loadingUsers}>
            Välj alla
          </Button>
          <Button size="small" onClick={() => updateAllowedUsers([])} disabled={loadingUsers}>
            Avmarkera alla
          </Button>
        </div>
        {!loadingUsers && selectedUserIDs.length === 0 && (
          <MessageBar className="p-2" intent="warning">
            <MessageBarBody>
              <Text>Ingen systemanvändare är vald. Ingen systemanvändare kommer att se ramavtalet efter nästa inloggning.</Text>
            </MessageBarBody>
          </MessageBar>
        )}
      </div>
      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        title={pendingValue ? "Aktivera dölj verksamhetslista?" : "Avaktivera dölj verksamhetslista?"}
        text={
          pendingValue
            ? "Är du säker på att du vill dölja verksamhetslistan? Kommunerna kommer inte längre att kunna se leverantörernas verksamhetsinformation i detta ramavtal."
            : "Är du säker på att du vill visa verksamhetslistan igen? Kommunerna kommer återigen kunna se leverantörernas verksamhetsinformation i detta ramavtal."
        }
      />
    </Card>
  );
};

export default TenderingVisibility;
